import * as Sentry from "@sentry/react";
import { format } from "date-fns";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL } from "../../../api/baseURL";
import {
  clearAllClaimFiles,
  buildDocumentTypesArray,
  collectDocumentsInOrder,
  submitClaimToVista,
} from "../../../api/vistaService";
import forwardarrow from "../../../assets/images/arrow_forwardb.svg";
import Loader from "../../../components/common/Loader/Loader";
import CustomCheckbox from "../../../components/common/customCheckbox/customCheckbox";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import useFileClaimFlow from "../../../hooks/useFileClaimFlow";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../hooks/useMotorFormData";
import { clearNewMotorFormData } from "../../../redux/features/newMotorFormSlice";
import { selectProfileData } from "../../../redux/features/profileSlice";
import FileClaimMotorLayout from "./fileClaimMotorLayout";

export const CaseNo = "CaseNumber";

// Merge the Vista validate-only extractions stamped on a slot's files
// (file._vistaExtraction, set by the upload pages on verified documents).
// Front and back of one document extract different field sets, so later
// files fill the gaps; empty values never overwrite real ones.
const mergeVistaExtractions = (docs) =>
  (docs || []).reduce((merged, doc) => {
    Object.entries(doc?._vistaExtraction || {}).forEach(([key, value]) => {
      const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0);
      if (!isEmpty) merged[key] = value;
    });
    return merged;
  }, {});

// "2026-07-19" + "17:00" (or "09:27 PM", as e-traffic intimations come
// back from Vista) → "2026-07-19T17:00:00+03:00". CRM stores a bare
// datetime as UTC (3h skew), so the Bahrain offset must ride along; a
// missing/unparseable time falls back to midnight rather than dropping the
// date entirely.
const buildVistaAccidentDate = (accidentDate, accidentTime) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(accidentDate || "")) return null;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/.exec(
    (accidentTime || "").trim()
  );
  let time = "00:00:00";
  if (match) {
    let hours = Number(match[1]);
    const meridiem = (match[4] || "").toUpperCase();
    if (meridiem === "PM" && hours < 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;
    time = `${String(hours).padStart(2, "0")}:${match[2]}:${match[3] || "00"}`;
  }
  return `${accidentDate}T${time}+03:00`;
};

function ReviewAndConfirmPage({
  selectedCustomer,
  selectedInsuredType,
  selectedClaimType,
  driverDocs,
  incidentDoc,
  accidentDocs,
  insuranceCert,
  propertyOwner,
  proofAddress,
  impactedPersonCprDocs,
  judicialReportDocs,
  otherDocs,
  AccidentInfoDetails,
  claimInfo,
  propertyAccidentPhotosDocs,
  selectedTpType,
  propertyDamageInfo,
  ownerShipDocs,
  sessionRef,
  resolvedPolicyNumber,
}) {
  const [caseNo, setCaseNo] = useState({});
  const { saveMotorDataInRedux } = useMotorForm(CaseNo, setCaseNo);

  //   if (judicialReportDocs && judicialReportDocs.length > 0 && judicialReportDocs[0].preview) {
  //   console.log("I am the owner " + judicialReportDocs?.[0].preview);
  // } else {
  //   var checking = judicialReportDocs?.[0]?.preview ?? '';
  //   console.log("propertyOwner is undefined, empty, or missing 'preview'");
  // }
  const [isChecked, setIsChecked] = useState(false);
  const [validationError, setValidationError] = useState("");
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const [isLoading, setIsLoading] = useState(false);
  // One submission at a time (Vista handover 2026-09-14 §3, CLM-06). The
  // button used to come back to life the moment createclaim answered, while
  // the file links and the Vista submit were still running with no spinner —
  // and a second click 6-20 s later made a second CRM claim. `isLoading` is
  // render-time state, so a ref is what actually stops the re-entry; the
  // state only drives the spinner.
  const submitInFlightRef = useRef(false);
  // The CRM claim once createclaim has succeeded. A retry after a later step
  // failed reuses it rather than creating the claim again.
  const crmClaimRef = useRef(null);
  const { handleNextSubstep } = useFileClaimFlow();
  const continueClickHanlder = () => {
    handleNextSubstep();
  };
  const dispatch = useDispatch();
  const profileData = useSelector(selectProfileData);

  // A failed step of the submission used to end in the customer's console
  // and nowhere else — claims found the 13 Sep failures in CRM days later
  // (Vista handover 2026-09-14 item 4). One Sentry event per failed step,
  // grouped by step, with the claim number as a searchable tag. Sentry's
  // PostHog integration forwards "error" level, so it lands in both. No
  // PII: a case number, an HTTP status and Vista's error code only.
  const reportSubmitStepFailure = (
    step,
    { crmCaseNumber, status, vistaError } = {}
  ) => {
    const tags = {
      step,
      insuredType: selectedInsuredType || "",
      customerType: selectedCustomer || "",
    };
    if (crmCaseNumber) tags.crmCaseNumber = String(crmCaseNumber);
    if (status !== undefined && status !== null) tags.status = String(status);
    if (vistaError) tags.vistaError = String(vistaError).slice(0, 200);
    console.error("[claim_submit_step_failed]", tags);
    try {
      Sentry.captureMessage("claim_submit_step_failed", {
        level: "error",
        tags,
        fingerprint: ["claim_submit_step_failed", step],
      });
    } catch (reportError) {
      // Telemetry must never break the submission.
    }
  };

  // How a failed fetch is labelled: its HTTP status, or why there was none.
  const failureStatus = (error) =>
    error?.status ??
    (error?.name === "AbortError" ? "timeout" : "network");

  // Validate mandatory claim data before submission (phone is required in all flows)
  const validateClaimData = () => {
    const isPropertyOrBodily =
      selectedInsuredType === "PROPERTY_DAMAGE" ||
      selectedInsuredType === "BODILY_INJURY";

    const phone = isPropertyOrBodily
      ? propertyDamageInfo?.mobile
      : claimInfo?.mobile;

    if (!phone || phone.trim() === "") {
      console.error("[validateClaimData] Missing mandatory field: phone");
      setValidationError(
        t("motorInsurance.fileAClaimFlow.reviewAndConfirmPage.validation_error_phone") ||
        "Phone number is required to process your claim. Please go back and fill in your contact information."
      );
      return false;
    }

    setValidationError("");
    return true;
  };

  const handleCaimSubmit = async () => {
    if (submitInFlightRef.current) return;
    // Validate mandatory fields before submission
    if (!validateClaimData()) {
      console.error("[handleCaimSubmit] Validation failed, not submitting claim");
      return;
    }

    submitInFlightRef.current = true;
    setIsLoading(true);
    try {
      var claim = crmClaimRef.current;
      if (claim) {
        console.log(
          "[handleCaimSubmit] Reusing CRM claim from the earlier attempt:",
          claim.CaseNumber
        );
      } else {
        claim = await CreateClaimAPI();
        console.log("[handleCaimSubmit] Claim result:", claim);
        if (claim?.CaseNumber) crmClaimRef.current = claim;
      }

      if (claim?.CaseNumber) {
        setCaseNo(claim?.CaseNumber);

        // Submit to Vista API with the CRM Case Number and GUID
        console.log(
          "[handleCaimSubmit] Calling Vista API with case number:",
          claim.CaseNumber,
          "and GUID:",
          claim.id
        );
        await submitToVistaAPI(claim.CaseNumber, claim.id);

        // The wizard's data is cleared only once the whole sequence is over,
        // so a retry after a failed step still has the documents to send
        // (the props this page reads come from newMotorFormData). The case
        // number is written after the clear: createdClaimPage reads it.
        dispatch(clearNewMotorFormData());
        saveMotorDataInRedux(claim?.CaseNumber);
        continueClickHanlder();
      } else {
        console.error(
          "[handleCaimSubmit] No CaseNumber in claim response, Vista API not called"
        );
        setValidationError(
          translate(
            "We could not submit your claim. Please try again.",
            "لم نتمكن من إرسال مطالبتك. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } finally {
      // Whole sequence over — success (navigated away) or failure (message
      // shown) — before the button is clickable again.
      submitInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const submitToVistaAPI = async (caseNumber, crmGuid) => {
    console.log("[Vista] submitToVistaAPI called with caseNumber:", caseNumber, "crmGuid:", crmGuid);
    try {
      // Get customer name based on claim type. Vista shows customerName
      // verbatim to adjusters, so prefer the logged-in profile's real name;
      // CPR is the fallback for third-party claimants with no profile.
      const profileName = [profileData?.FirstName, profileData?.LastName]
        .filter(Boolean)
        .join(" ");
      const customerName =
        selectedInsuredType === "PROPERTY_DAMAGE" ||
        selectedInsuredType === "BODILY_INJURY"
          ? propertyDamageInfo?.ownerName || "Customer"
          : profileName || claimInfo?.ownerCprNumber || "Customer";

      // Build uploaded files object from props
      const uploadedFiles = {
        driverDocs,
        ownerShipDocs,
        accidentDocs,
        incidentDoc,
        insuranceCert,
        propertyOwner,
        proofAddress,
        propertyAccidentPhotosDocs,
        impactedPersonCprDocs,
        judicialReportDocs,
        otherDocs,
      };

      // Build document types dynamically based on actual uploaded files
      const documentTypes = buildDocumentTypesArray(
        uploadedFiles,
        selectedInsuredType,
        selectedCustomer
      );

      // Collect document files in the same order as documentTypes
      const documents = collectDocumentsInOrder(
        uploadedFiles,
        selectedInsuredType,
        selectedCustomer
      );

      console.log("[Vista] Customer name:", customerName);
      console.log("[Vista] Document types:", documentTypes);
      console.log("[Vista] Documents count:", documents.length);
      console.log("[Vista] Documents:", documents);

      // Verify counts match
      if (documentTypes.length !== documents.length) {
        console.warn(`[Vista] Mismatch: ${documentTypes.length} types vs ${documents.length} documents`);
      }

      const isPropertyOrBodily =
        selectedInsuredType === "PROPERTY_DAMAGE" ||
        selectedInsuredType === "BODILY_INJURY";

      // Customer's real policy number when resolvable (plate matched against
      // the logged-in user's motor policies); fall back to the plate number so
      // Vista's create-mode required field and rate-limit identity stay stable.
      const policyNumber =
        resolvedPolicyNumber || claimInfo?.plateNumber || caseNumber;

      // Who was driving: derivable only from the collected CPRs
      const whoWasDriving =
        !isPropertyOrBodily &&
        claimInfo?.driverCprNumber &&
        claimInfo?.ownerCprNumber
          ? claimInfo.driverCprNumber === claimInfo.ownerCprNumber
            ? "Owner"
            : "Other driver"
          : null;

      // Submit to Vista API
      console.log("[Vista] Calling submitClaimToVista...");
      const vistaResult = await submitClaimToVista({
        customerName,
        policyNumber,
        crmCaseNumber: caseNumber,
        documentTypes,
        documents,
        crmGuid,
        sessionRef,
        driverCpr: isPropertyOrBodily ? null : claimInfo?.driverCprNumber,
        accidentDescription:
          selectedInsuredType === "BODILY_INJURY"
            ? propertyDamageInfo?.accidentDesc
            : AccidentInfoDetails?.accidentDesc,
        preferredGarage:
          AccidentInfoDetails?.Garage || AccidentInfoDetails?.workshop || null,
        settlementMethod: selectedClaimType || null,
        whoWasDriving,
        crmApiBase: BASE_URL,
      });

      console.log("[Vista] Submission successful:", vistaResult);

      // Vista writes the report link on the CRM claim itself (crmApiBase
      // above), so nothing is left for this page to save.
      if (!vistaResult?.case?.reportUrl) {
        reportSubmitStepFailure("vista_report_missing", {
          crmCaseNumber: caseNumber,
          vistaError: vistaResult?.error || vistaResult?.case?.status,
        });
      }

      // Clear claim files after successful submission
      clearAllClaimFiles();
    } catch (error) {
      // Log error but don't block the flow - Vista is supplementary
      console.error("[Vista] Error submitting to Vista:", error);
      reportSubmitStepFailure("vista_full", {
        crmCaseNumber: caseNumber,
        status: failureStatus(error),
        vistaError: error?.vistaError,
      });
    }
  };

  const buildDocumentData = (guid) => {
    const getFileUrl = (arr, index) =>
      arr?.[index]?.preview || arr?.[index]?.path || "";

    return {
      GuidId: guid,
      Cpr1: getFileUrl(propertyOwner, 0),
      Cpr2: getFileUrl(propertyOwner, 1),
      propertyProof: getFileUrl(ownerShipDocs, 0),
      OwnerShip1: getFileUrl(proofAddress, 0) || getFileUrl(ownerShipDocs, 0),
      OwnerShip2: getFileUrl(proofAddress, 1) || getFileUrl(ownerShipDocs, 1),
      AccidentPhoto:
        getFileUrl(propertyAccidentPhotosDocs, 0) ||
        getFileUrl(accidentDocs, 0),
      AccidentPhoto2:
        getFileUrl(propertyAccidentPhotosDocs, 1) ||
        getFileUrl(accidentDocs, 1),
      AccidentPhoto3:
        getFileUrl(propertyAccidentPhotosDocs, 2) ||
        getFileUrl(accidentDocs, 2),
      AccidentPhoto4:
        getFileUrl(propertyAccidentPhotosDocs, 3) ||
        getFileUrl(accidentDocs, 3),
      AccidentPhoto5:
        getFileUrl(propertyAccidentPhotosDocs, 4) ||
        getFileUrl(accidentDocs, 4),
      AccidentPhoto6:
        getFileUrl(propertyAccidentPhotosDocs, 5) ||
        getFileUrl(accidentDocs, 5),
      AccidentPhoto7:
        getFileUrl(propertyAccidentPhotosDocs, 6) ||
        getFileUrl(accidentDocs, 6),
      AccidentPhoto8:
        getFileUrl(propertyAccidentPhotosDocs, 7) ||
        getFileUrl(accidentDocs, 7),
      AccidentPhoto9:
        getFileUrl(propertyAccidentPhotosDocs, 8) ||
        getFileUrl(accidentDocs, 8),
      AccidentPhoto10:
        getFileUrl(propertyAccidentPhotosDocs, 9) ||
        getFileUrl(accidentDocs, 9),
      Driving1:
        selectedInsuredType === "PROPERTY_DAMAGE"
          ? getFileUrl(propertyOwner, 0)
          : selectedInsuredType === "PROPERTY_DAMAGE"
          ? getFileUrl(impactedPersonCprDocs, 0)
          : getFileUrl(driverDocs, 0),
      Driving2:
        selectedInsuredType === "PROPERTY_DAMAGE"
          ? getFileUrl(propertyOwner, 1)
          : selectedInsuredType === "PROPERTY_DAMAGE"
          ? getFileUrl(impactedPersonCprDocs, 1)
          : getFileUrl(driverDocs, 1),
      JudicialRuling: getFileUrl(judicialReportDocs, 0),
      JudicialRuling2: getFileUrl(judicialReportDocs, 1),
      JudicialRuling3: getFileUrl(judicialReportDocs, 2),
      JudicialRuling4: getFileUrl(judicialReportDocs, 3),
      JudicialRuling5: getFileUrl(judicialReportDocs, 4),
      OtherDocs:
        selectedInsuredType === "BODILY_INJURY"
          ? getFileUrl(otherDocs, 0)
          : getFileUrl(insuranceCert, 0),
      OtherDocs2:
        selectedInsuredType === "BODILY_INJURY"
          ? getFileUrl(otherDocs, 1)
          : getFileUrl(insuranceCert, 1),
      OtherDocs3:
        selectedInsuredType === "BODILY_INJURY"
          ? getFileUrl(otherDocs, 2)
          : getFileUrl(insuranceCert, 2),
      OtherDocs4:
        selectedInsuredType === "BODILY_INJURY"
          ? getFileUrl(otherDocs, 3)
          : getFileUrl(insuranceCert, 3),
      OtherDocs5:
        selectedInsuredType === "BODILY_INJURY"
          ? getFileUrl(otherDocs, 4)
          : getFileUrl(insuranceCert, 4),
      // CRM has five report fields, and the wizard's report slot is sized by
      // Vista's config — five today. Raising that slot past five in Vista's
      // admin would land the extra pages in Vista but not here, so CRM needs
      // the matching fields before the limit goes any higher.
      IncidentReport: getFileUrl(incidentDoc, 0),
      IncidentReport2: getFileUrl(incidentDoc, 1),
      IncidentReport3: getFileUrl(incidentDoc, 2),
      IncidentReport4: getFileUrl(incidentDoc, 3),
      IncidentReport5: getFileUrl(incidentDoc, 4),
      PolicyInformation: getFileUrl(insuranceCert, 0),
    };
  };

  // OCR enrichment for the CRM claim (field-mapping handover v2,
  // 2026-07-27): motor vehicle-damage claims carry the fields Vista
  // extracted during the wizard's validate-only checks. Raw text goes
  // verbatim — WebAPI resolves make/model/body type/location server-side
  // and derives NatureOfClaim + subrogation from GdtDrivers (forward ALL
  // drivers, unfiltered). Every field is optional server-side, so missing
  // extractions are simply omitted.
  const buildVistaCrmFields = () => {
    if (
      selectedInsuredType == "PROPERTY_DAMAGE" ||
      selectedInsuredType == "BODILY_INJURY"
    )
      return {};
    const license = mergeVistaExtractions(driverDocs);
    const ownership = mergeVistaExtractions(ownerShipDocs);
    const gdt = mergeVistaExtractions(incidentDoc);
    // A non-Bahraini accident report (Vista documentValidation.foreign,
    // stamped on the file by the upload pages): its address must not be
    // resolved into a Bahraini governorate/city by WebAPI, so the location
    // is withheld. Everything else, GdtDrivers included, still goes.
    const foreignGdt = (incidentDoc || []).some((file) => file?._vistaForeign);
    // Only a physical (station) report carries the final traffic report
    // number: an e-traffic intimation's number must not land in CRM's
    // gits_policereportnumber, nor should Najm/civil-defence ("other")
    // numbers (Vista handover 2026-08-17 round 2 §3 M2). `undefined` means
    // Vista has not shipped reportKind yet — keep sending until it does.
    const reportKind = (incidentDoc || [])
      .map((file) => file?._vistaReportKind)
      .find((kind) => kind !== undefined);
    const candidates = {
      VinChassisNo: ownership.chassisNo,
      VehicleYear:
        ownership.yearOfMake != null ? String(ownership.yearOfMake) : null,
      PoliceReportNumber:
        reportKind === undefined || reportKind === "physical"
          ? gdt.policeReportNumber
          : null,
      AccidentDate: buildVistaAccidentDate(gdt.accidentDate, gdt.accidentTime),
      DriverDateOfBirth: license.birthDate,
      VehicleMakeText: ownership.make,
      VehicleModelText: ownership.model,
      VehicleBodyTypeText: ownership.vehicleType,
      AccidentLocationText: foreignGdt ? null : gdt.accidentLocation,
      GdtDrivers: Array.isArray(gdt.drivers) ? gdt.drivers : null,
    };
    const fields = {};
    Object.entries(candidates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "")
        fields[key] = value;
    });
    return fields;
  };

  const CreateClaimAPI = async () => {
    const url = BASE_URL + "/claimcrm/createclaim";
    // Ownership OCR also feeds ownerName/RegNumber: WebAPI matches RegNumber
    // against the GDT drivers' plates to spot the submitter (the typed plate
    // can miss), and claims want the English owner name. Property/bodily
    // flows carry no ownership docs, so both keep their current values there.
    const ownership =
      selectedInsuredType == "PROPERTY_DAMAGE" ||
      selectedInsuredType == "BODILY_INJURY"
        ? {}
        : mergeVistaExtractions(ownerShipDocs);
    const data = {
      ...buildVistaCrmFields(),
      // Your data to send in the request body
      isCustomer: selectedCustomer == "SOLIDARITY_CUSTOMER" ? true : false,
      CPR:
        selectedInsuredType == "PROPERTY_DAMAGE"
          ? ""
          : claimInfo?.driverCprNumber ?? "",
      ownerCPR:
        selectedInsuredType == "PROPERTY_DAMAGE" ||
        selectedInsuredType == "BODILY_INJURY"
          ? propertyDamageInfo?.ownerCpr
          : claimInfo?.ownerCprNumber ?? "",
      cprImpacted: "",
      ownerName:
        selectedInsuredType == "PROPERTY_DAMAGE"
          ? propertyDamageInfo?.ownerName
          : ownership.ownerEn || ownership.owner || "",
      ownerAddress:
        selectedInsuredType == "PROPERTY_DAMAGE"
          ? propertyDamageInfo?.address
          : "",
      personForSurvey:
        selectedInsuredType == "PROPERTY_DAMAGE"
          ? AccidentInfoDetails?.mobile
          : "",
      Email:
        selectedInsuredType == "PROPERTY_DAMAGE" ||
        selectedInsuredType == "BODILY_INJURY"
          ? propertyDamageInfo?.email
          : claimInfo?.email ?? "",
      PhoneNumber:
        selectedInsuredType == "PROPERTY_DAMAGE" ||
        selectedInsuredType == "BODILY_INJURY"
          ? propertyDamageInfo?.mobile
          : claimInfo?.mobile ?? "",
      RegNumber:
        selectedInsuredType == "PROPERTY_DAMAGE"
          ? ""
          : ownership.vehicleNo || claimInfo?.plateNumber || "",
      desc:
        selectedInsuredType == "BODILY_INJURY"
          ? propertyDamageInfo?.accidentDesc
          : AccidentInfoDetails?.accidentDesc ?? "",
      SurveyDate:
        AccidentInfoDetails?.surveyDate != null
          ? new Date(AccidentInfoDetails?.surveyDate).toISOString()
          : "",
      IntimateDate: new Date().toISOString(),
      GarageCode: AccidentInfoDetails?.workshop || "Others",
      Source: "online",
      ClaimCompensation: selectedClaimType == "cash" ? 1 : 0,
      InsuredType:
        selectedTpType == "individualComprehensive"
          ? "IND"
          : selectedTpType == "individualThirdParty"
          ? "IND"
          : selectedTpType == "corporateClient"
          ? "CORP"
          : "",
      TPType: selectedCustomer == "THIRD_PARTY_CUSTOMER" ? "DirectTP" : "",
      ClaimOptions:
        selectedInsuredType == "VEHICLE_DAMAGE"
          ? "Vehicle"
          : selectedInsuredType == "PROPERTY_DAMAGE"
          ? "Property"
          : selectedInsuredType == "BODILY_INJURY"
          ? "Bodily"
          : "",
      InsuredCoverage:
        selectedTpType == "individualComprehensive"
          ? "COMP"
          : selectedTpType == "individualThirdParty"
          ? "TP"
          : "",
      // The document links go with the create itself, so the claim is never
      // registered without them (CRM 122715/122716, 16 Sep); WebAPI writes
      // the filled ones. There is no separate updateclaim for them any more,
      // and Vista writes the report link itself.
      Attachments: buildDocumentData(null),
    };

    // Loading state and re-entry are handleCaimSubmit's: they span the whole
    // submission, not just this request.
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        },
        body: JSON.stringify(data), // Convert data to JSON string
      });
      if (!response.ok) {
        const error = new Error("Network response was not ok");
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log("Success:", result);
      return result;
    } catch (error) {
      console.error("Error:", error);
      reportSubmitStepFailure("createclaim", { status: failureStatus(error) });
    }
  };

  const declaration = t(
    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.declaration",
    { returnObjects: true }
  );

  console.log("AccidentInfoDetails&&&&&&&", AccidentInfoDetails);
  return (
    <FileClaimMotorLayout
      is_bottom_sticky_layout={false}
      btn_onClick={continueClickHanlder}
    >
      <div className="mx-auto  bg-white max767:p-4 max767:pt-5 w-[500px] max-w-[100%]">
        <TitleDescription
          className="max767:hidden"
          title={t("motorInsurance.fileAClaimFlow.reviewAndConfirmPage.title")}
          description={t(
            "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.description"
          )}
        />
        <div className="max767:block hidden max767:mb-6 max767:mt-0">
          <TitleDescription
            title={t(
              "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.mobile_title"
            )}
            description={t(
              "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.mobile_description"
            )}
          />
        </div>
        <div className="flex flex-col items-start gap-6 self-stretch bg-[#F6F8F9] p-4 rounded-[26px]">
          <table className="w-full table-auto border-separate border border-solid border-[#DDE2E4] border-spacing-0 bg-white rounded-[10px]">
            <tbody>
              {/* Table rows for each piece of information */}
              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.claim_type_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {translate("Motor Policy", "وثيقة تأمين المركبة")}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedCustomer == "THIRD_PARTY_CUSTOMER" ? "hidden" : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.policy_plan_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedTpType == "individualComprehensive"
                    ? "Individual Comprehensive"
                    : selectedTpType == "individualThirdParty"
                    ? "Individual Third Party"
                    : selectedTpType == "corporateClient"
                    ? "Corporate Client"
                    : ""}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedClaimType == "BODILY_INJURY"
                    ? "hidden"
                    : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.driver_cpr_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {claimInfo?.driverCprNumber}
                </td>
              </tr>

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.vehicle_cpr_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? propertyDamageInfo?.ownerCpr
                    : claimInfo?.ownerCprNumber}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? "hidden"
                    : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.vehicle_plate_num_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {claimInfo?.plateNumber}
                </td>
              </tr>

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.email_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? propertyDamageInfo?.email
                    : claimInfo?.email}
                </td>
              </tr>

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.mobile_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? propertyDamageInfo?.mobile
                    : claimInfo?.mobile}
                </td>
              </tr>

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.accident_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedInsuredType == "BODILY_INJURY"
                    ? propertyDamageInfo?.accidentDesc
                    : AccidentInfoDetails?.accidentDesc}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? "hidden"
                    : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.preferred_garage_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {AccidentInfoDetails?.Garage}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "BODILY_INJURY" ? "hidden" : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.preferred_repair_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {selectedInsuredType == "BODILY_INJURY"
                    ? "hidden"
                    : AccidentInfoDetails?.surveyDate
                    ? format(
                        new Date(AccidentInfoDetails.surveyDate),
                        "dd/MM/yyyy"
                      )
                    : "-"}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? "hidden"
                    : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.driver_licence_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {driverDocs?.[0]?.path ? driverDocs?.[0].path : ""}{" "}
                  {driverDocs?.[1]?.path ? driverDocs?.[1].path : ""}
                </td>
              </tr>

              <tr
                className={`border-b ${
                  selectedInsuredType == "PROPERTY_DAMAGE" ||
                  selectedInsuredType == "BODILY_INJURY"
                    ? "hidden"
                    : ""
                }`}
              >
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.vehicle_owner_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {ownerShipDocs?.[0]?.path ? ownerShipDocs?.[0].path : ""}{" "}
                  {ownerShipDocs?.[1]?.path ? ownerShipDocs?.[1].path : ""}
                </td>
              </tr>

              {/* Third-party vehicle damage: the claimant's own insurance
                  certificate, uploaded on its wizard page. */}
              {selectedCustomer == "THIRD_PARTY_CUSTOMER" &&
                selectedInsuredType == "VEHICLE_DAMAGE" && (
                  <tr className="border-b">
                    <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                      {t(
                        "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.insurance_certificate_label"
                      )}
                    </td>
                    <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                      {(insuranceCert || [])
                        .map((doc) => doc?.path)
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                  </tr>
                )}

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.photos_of_accident_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {accidentDocs?.[0]?.path ? accidentDocs?.[0].path : ""}{" "}
                  {accidentDocs?.[1]?.path ? accidentDocs?.[1].path : ""}{" "}
                  {propertyAccidentPhotosDocs?.[0]?.path
                    ? propertyAccidentPhotosDocs?.[0].path
                    : ""}{" "}
                  {propertyAccidentPhotosDocs?.[1]?.path
                    ? propertyAccidentPhotosDocs?.[1].path
                    : ""}
                </td>
              </tr>

              <tr className="border-b">
                <td className="border-b border-r py-2 px-4 text-[#5B6871] text-sm not-italic font-medium leading-6">
                  {t(
                    "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.incident_gov_label"
                  )}
                </td>
                <td className="border-b py-2 px-4 text-[#48535B] text-sm not-italic font-medium leading-6">
                  {/* Every page of the report, not just the first: a station
                      report arrives as several photographed pages, and a row
                      naming one of them reads as though the rest were lost. */}
                  {(incidentDoc || [])
                    .map((doc) => doc?.path)
                    .filter(Boolean)
                    .join(" ")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Terms and conditions note */}
        <div className="text-sm mt-6 flex mb-6 justify-start chekcboxdiv">
          <CustomCheckbox
            Label={
              <div className="flex">
                <label
                  htmlFor="agreement"
                  className="text-[#48535B] text-sm not-italic font-normal leading-[22px] pl-3"
                >
                  {declaration?.map((paragraph, index) => (
                    <p className="mb-2" key={index}>
                      {paragraph}
                    </p>
                  ))}
                </label>
              </div>
            }
            isChecked={isChecked}
            setIsChecked={setIsChecked}
          />
        </div>
        {validationError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-[10px] text-sm">
            {validationError}
          </div>
        )}
        <button
          className={`w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px] ${
            isChecked && !isLoading
              ? "bg-[#55C6EF] cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          onClick={handleCaimSubmit}
          disabled={!isChecked || isLoading}
        >
          <span>
            {t(
              "motorInsurance.fileAClaimFlow.reviewAndConfirmPage.submitClaimBtnLabel"
            )}
          </span>
          {isLoading ? (
            <div className="w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2">
              <Loader />
            </div>
          ) : (
            <img
              src={forwardarrow}
              className="w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"
              alt="forward arrow"
            />
          )}
        </button>
      </div>
    </FileClaimMotorLayout>
  );
}

export default ReviewAndConfirmPage;
